import React, { useEffect, useState } from 'react';
import SubmitButton from '../../components/SubmitButton';
import {
  Order,
  PartnerUser,
  Warehouse,
  AvailableCouriersResponse,
  WarehousesResponse,
  OrderAssignmentResponse,
} from '../../models/APIModels';
import Label from '../../components/Label';
import { axiosKK } from '../../services/networkRequest';
import Modal from 'react-modal';
import { Formik } from 'formik';
import errorMessage from '../../services/errorMessage';

interface OrderModalProps {
  update: (updated: Order) => void;
  close: () => void;
  editMode: boolean;
  order: Order;
}

interface AssignLogisticsValues {
  courierId: number | '';
  warehouseId: number | '';
}

interface AssignLogisticsErrors {
  courierId?: string;
  warehouseId?: string;
}

export default function OrderEditAssignLogisticsModal({
  editMode,
  order,
  close,
  update,
}: OrderModalProps): JSX.Element {
  const [couriers, setCouriers] = useState<PartnerUser[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [currentCourier, setCurrentCourier] = useState<PartnerUser | undefined>();
  const [currentWarehouse, setCurrentWarehouse] = useState<Warehouse | undefined>();
  const [networkError, setNetworkError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<AssignLogisticsValues>({
    courierId: '',
    warehouseId: '',
  });

  useEffect(() => {
    if (editMode) {
      setLoading(true);
      setNetworkError(undefined);

      Promise.all([
        axiosKK.get<AvailableCouriersResponse>('/admin/logistics/couriers'),
        axiosKK.get<WarehousesResponse>('/admin/logistics/warehouses'),
        axiosKK.get<OrderAssignmentResponse>(
          `/admin/logistics/orders/${order.id}/assignment`
        ),
      ])
        .then(([couriersRes, warehousesRes, assignmentRes]) => {
          setCouriers(couriersRes.data.couriers || []);
          setWarehouses(warehousesRes.data.warehouses || []);

          const assignment = assignmentRes.data;
          setCurrentCourier(assignment.courier);
          setCurrentWarehouse(assignment.warehouse);

          setInitialValues({
            courierId: assignment.courier?.id || '',
            warehouseId: assignment.warehouse?.id || '',
          });
        })
        .catch((error) => {
          errorMessage(setNetworkError)(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [editMode, order.id]);

  return (
    <Modal
      isOpen={editMode}
      onRequestClose={close}
      style={{
        content: {
          display: 'flex',
        },
      }}
      contentLabel="Assign Logistics"
    >
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validate={(values: AssignLogisticsValues) => {
          const errors: AssignLogisticsErrors = {};
          if (!values.courierId && !values.warehouseId) {
            errors.courierId = 'Select at least a courier or warehouse';
          }
          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          setNetworkError(undefined);

          const payload: any = {};
          if (values.courierId) {
            payload.courierId = Number(values.courierId);
          }
          if (values.warehouseId) {
            payload.warehouseId = Number(values.warehouseId);
          }

          axiosKK
            .put<OrderAssignmentResponse>(
              `/admin/logistics/orders/${order.id}/assign`,
              payload
            )
            .then(({ data }) => {
              update(data.order);
              close();
            })
            .catch((error) => {
              errorMessage(setNetworkError)(error);
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {({
          values,
          isSubmitting,
          handleChange,
          handleSubmit,
          errors,
          isValid,
          dirty,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="order-edit-form assign-logistics-form"
            name="assign-logistics-form"
          >
            <div className="modal-header">
              <h3>Assign Logistics</h3>
              <SubmitButton red={true} onClick={close}>
                Close
              </SubmitButton>
            </div>
            <div className="modal-content">
              {loading ? (
                <p>Loading available options...</p>
              ) : (
                <>
                  {(currentCourier || currentWarehouse) && (
                    <div className="order-edit-inputs">
                      <Label>Current Assignment</Label>
                      <div className="current-info">
                        <div className="current-label">
                          <h6>Current</h6>
                        </div>
                        <div className="info">
                          <span>
                            {currentCourier
                              ? `Courier: ${currentCourier.firstName} ${currentCourier.lastName}`
                              : 'Courier: Not assigned'}
                            {' | '}
                            {currentWarehouse
                              ? `Warehouse: ${currentWarehouse.name}`
                              : 'Warehouse: Not assigned'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="order-edit-inputs">
                    <Label htmlFor="courierId">Courier</Label>
                    {errors.courierId && (
                      <span className="error">{errors.courierId}</span>
                    )}
                    <select
                      className="form-select"
                      id="courierId"
                      name="courierId"
                      value={values.courierId}
                      onChange={handleChange}
                    >
                      <option value="" disabled>Select a courier</option>
                      {couriers.map((courier) => {
                        const displayName = [courier.firstName, courier.lastName].filter(Boolean).join(' ') || courier.email || `Courier #${courier.id}`;
                        return (
                          <option key={courier.id} value={courier.id}>
                            {displayName}
                            {courier.phoneNumber ? ` (${courier.phoneNumber})` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="order-edit-inputs">
                    <Label htmlFor="warehouseId">Warehouse</Label>
                    <select
                      className="form-select"
                      id="warehouseId"
                      name="warehouseId"
                      value={values.warehouseId}
                      onChange={handleChange}
                    >
                      <option value="" disabled>Select a warehouse</option>
                      {warehouses.map((warehouse) => {
                        const displayName = warehouse.name || `Warehouse #${warehouse.id}`;
                        const location = [warehouse.city, warehouse.postcode].filter(Boolean).join(', ');
                        return (
                          <option key={warehouse.id} value={warehouse.id}>
                            {displayName}{location ? ` - ${location}` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </>
              )}
            </div>
            {networkError && <span className="error">{networkError}</span>}
            <SubmitButton
              disabled={loading || !isValid || (!dirty && !!currentCourier)}
              isSubmitting={isSubmitting}
            >
              Assign
            </SubmitButton>
          </form>
        )}
      </Formik>
    </Modal>
  );
}
